import { cn } from '@commander/utils';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { ChevronDownIcon as ChevronDown, XIcon as X } from '../../icons';

const chipVariants = cva(
  'inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-xs font-rooftop font-medium transition-all duration-300 ease-in-out',
  {
    variants: {
      variant: {
        // Status chips from Commander design (solid colors)
        engaged:
          '[background:hsl(var(--chip-engaged))] text-white hover:[background:hsl(var(--chip-engaged)/90)]',
        targeting:
          '[background:hsl(var(--chip-targeting))] text-white hover:[background:hsl(var(--chip-targeting)/90)]',
        customer:
          '[background:hsl(var(--chip-customer))] text-white hover:[background:hsl(var(--chip-customer)/90)]',
        pending:
          '[background:hsl(var(--chip-pending))] text-white hover:[background:hsl(var(--chip-pending)/90)]',
        recommended:
          '[background:hsl(var(--chip-recommended))] text-white hover:[background:hsl(var(--chip-recommended)/90)]',
        noData:
          '[background:hsl(var(--chip-no-data))] text-white hover:[background:hsl(var(--chip-no-data)/90)]',
        assigned:
          '[background:hsl(var(--badge-prospects))] text-white hover:[background:hsl(var(--badge-prospects)/90)]',

        // Light mode variants
        engagedLight:
          '[background:hsl(var(--chip-engaged)/10)] [color:hsl(var(--chip-engaged))] border [border-color:hsl(var(--chip-engaged)/20)] hover:[background:hsl(var(--chip-engaged)/20)]',
        targetingLight:
          '[background:hsl(var(--chip-targeting)/10)] [color:hsl(var(--chip-targeting))] border [border-color:hsl(var(--chip-targeting)/20)] hover:[background:hsl(var(--chip-targeting)/20)]',
        customerLight:
          '[background:hsl(var(--chip-customer)/10)] [color:hsl(var(--chip-customer))] border [border-color:hsl(var(--chip-customer)/20)] hover:[background:hsl(var(--chip-customer)/20)]',

        // Default
        default: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
      },
      size: {
        small: 'px-2 py-1 text-[10px]',
        default: 'px-3 py-1.5 text-xs',
        large: 'px-4 py-2 text-sm',
      },
      interactive: {
        true: 'cursor-pointer',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
      interactive: false,
    },
  }
);

export interface ChipProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof chipVariants> {
  onRemove?: () => void;
  hasDropdown?: boolean;
  dropdownItems?: Array<{
    label: string;
    value: string;
    onClick?: () => void;
  }>;
}

const Chip = React.forwardRef<HTMLDivElement, ChipProps>(
  (
    {
      className,
      variant,
      size,
      interactive,
      onRemove,
      hasDropdown = false,
      dropdownItems = [],
      children,
      ...props
    },
    ref
  ) => {
    if (hasDropdown && dropdownItems.length > 0) {
      return (
        <DropdownMenuPrimitive.Root>
          <DropdownMenuPrimitive.Trigger asChild>
            <div
              ref={ref}
              className={cn(chipVariants({ variant, size, interactive: true }), className)}
              {...props}
            >
              {children}
              <ChevronDown className="h-3 w-3 ml-1" />
            </div>
          </DropdownMenuPrimitive.Trigger>
          <DropdownMenuPrimitive.Portal>
            <DropdownMenuPrimitive.Content
              className="z-50 min-w-[8rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md animate-in fade-in-80"
              sideOffset={5}
            >
              {dropdownItems.map((item) => (
                <DropdownMenuPrimitive.Item
                  key={item.value}
                  className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                  onSelect={item.onClick}
                >
                  {item.label}
                </DropdownMenuPrimitive.Item>
              ))}
            </DropdownMenuPrimitive.Content>
          </DropdownMenuPrimitive.Portal>
        </DropdownMenuPrimitive.Root>
      );
    }

    return (
      <div
        ref={ref}
        className={cn(chipVariants({ variant, size, interactive }), className)}
        {...props}
      >
        {children}
        {onRemove && (
          <button
            onClick={onRemove}
            className="ml-1 rounded hover:bg-black/10 dark:hover:bg-white/10"
            aria-label="Remove"
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>
    );
  }
);
Chip.displayName = 'Chip';

// Status Chip Component (with dropdown)
export interface StatusChipProps extends Omit<ChipProps, 'variant'> {
  status: 'engaged' | 'targeting' | 'customer' | 'pending' | 'recommended' | 'noData' | 'assigned';
  onStatusChange?: (newStatus: string) => void;
  availableStatuses?: Array<{
    label: string;
    value: string;
  }>;
}

export function StatusChip({
  status,
  onStatusChange,
  availableStatuses = [
    { label: 'Engaged', value: 'engaged' },
    { label: 'Targeting', value: 'targeting' },
    { label: 'Customer', value: 'customer' },
    { label: 'Pending', value: 'pending' },
    { label: 'Recommended', value: 'recommended' },
    { label: 'No Data', value: 'noData' },
  ],
  className,
  ...props
}: StatusChipProps) {
  const dropdownItems = availableStatuses.map((item) => ({
    ...item,
    onClick: () => onStatusChange?.(item.value),
  }));

  const statusLabels = {
    engaged: 'Engaged',
    targeting: 'Targeting',
    customer: 'Customer',
    pending: 'Pending',
    recommended: 'Recommended',
    noData: 'No Data',
    assigned: 'Assigned to',
  };

  return (
    <Chip
      variant={status}
      hasDropdown={!!onStatusChange}
      dropdownItems={dropdownItems}
      className={className}
      {...props}
    >
      {statusLabels[status]}
    </Chip>
  );
}

export { Chip, chipVariants };
